import React, { useState } from "react";
import { Box, Typography, Avatar, Checkbox, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_AI_EVIDENCE_OWNERS_FOR_CHECK,
  REQUEST_AI_EVIDENCE_REVIEW,
} from "../../apis/integrated-evidence/aiEvidenceApis";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import ConfirmSnackbar from "../Snackbar/ConfirmSnackbar";
import { initials } from "../../utils/aiEvidenceOwners";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../AccessControl";

/**
 * Asks a data owner to review the suggestions they already manage.
 *
 * The mirror of the access request: that one asks to be given access, this
 * one nudges whoever already has it. Pass a checkId to scope the email to a
 * single check; without one it is scoped to the owner's integrations.
 */
const RequestReviewDialog = ({ open, onClose, checkId }) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const info = tc.status.info;

  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );

  const { data, loading } = useQuery(GET_AI_EVIDENCE_OWNERS_FOR_CHECK, {
    variables: checkId ? { checkId } : {},
    skip: !aiEvidenceEnabled || !open,
    fetchPolicy: "cache-first",
  });
  const owners = data?.getAIEvidenceOwnersForCheck || [];
  const multiple = owners.length > 1;

  const [requestReview, { loading: sending }] = useMutation(
    REQUEST_AI_EVIDENCE_REVIEW,
  );

  const toggleOwner = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClose = () => {
    if (sending) return;
    setSelected(new Set());
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    setError(null);
    // A lone owner has no checkbox to tick, so it is implied.
    const ownerIds = multiple
      ? Array.from(selected)
      : owners.map((o) => o.employeeDataId);
    try {
      const res = await requestReview({
        variables: { checkId: checkId ?? null, ownerIds },
      });
      const result = res?.data?.requestAIEvidenceReview;
      if (result?.success) {
        const names = owners
          .filter((o) => ownerIds.includes(o.employeeDataId))
          .map((o) => o.name || o.email)
          .filter(Boolean);
        const recipients =
          names.length === 1
            ? names[0]
            : names.length === 2
              ? `${names[0]} and ${names[1]}`
              : `${names[0]} and ${names.length - 1} others`;
        setSelected(new Set());
        onClose();
        setSnackbarInfo({
          message: `Review request sent to ${recipients}`,
          vertical: "top",
          horizontal: "right",
          key: Date.now(),
        });
      } else {
        setError(result?.message || "Could not send the request.");
      }
    } catch (err) {
      setError("Could not send the request.");
    }
  };

  const noOwners = !loading && owners.length === 0;

  return (
    <>
      <ConfirmDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        loading={sending}
        confirmDisabled={noOwners || (multiple && selected.size === 0)}
        heading="Ask for a review"
        buttonText="Send request"
        note={error}
        content={
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: tc.text.secondary,
                mb: multiple ? 2 : 0,
              }}
            >
              {noOwners
                ? "There are no data owners to ask right now."
                : multiple
                  ? "Choose who to ask. We'll email them that these suggestions are waiting on their review."
                  : `We'll email ${
                      owners[0]?.name || owners[0]?.email
                    } that these suggestions are waiting on their review.`}
            </Typography>

            {multiple && (
              <Box
                sx={{
                  border: `1px solid ${tc.border.subtle}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {owners.map((owner, index) => (
                  <Box
                    key={owner.employeeDataId || index}
                    onClick={() => toggleOwner(owner.employeeDataId)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      borderBottom:
                        index < owners.length - 1
                          ? `1px solid ${tc.border.subtle}`
                          : "none",
                      "&:hover": { backgroundColor: tc.background.hover },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selected.has(owner.employeeDataId)}
                      sx={{ p: 0.5 }}
                    />
                    <Avatar
                      sx={{
                        width: 26,
                        height: 26,
                        fontSize: "11px",
                        fontWeight: 600,
                        bgcolor: alpha(info.main, 0.14),
                        color: info.text,
                      }}
                    >
                      {initials(owner.name || owner.email)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13.5px",
                          fontWeight: 500,
                          color: tc.text.strong,
                        }}
                      >
                        {owner.name || owner.email}
                      </Typography>
                      {owner.integrations && (
                        <Typography
                          sx={{ fontSize: "11.5px", color: tc.text.secondary }}
                        >
                          {owner.integrations}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        }
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default RequestReviewDialog;
